// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import './styles/main.css';
import { EventLog } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import {
  fromDataTransfer,
  fromDirectoryPicker,
  fromFileList,
  fromSample,
  commonPrefix,
  supportsDirectoryPicker,
  type Ingested,
} from './ingest.ts';
import { extractDoc, type ExtractedDoc } from './extract.ts';
import { docUrl, DEFAULT_CHUNK_OPTIONS, type UrlStyle } from './chunk.ts';
import type { WorkerRequest, WorkerResponse, WorkerStage } from './worker.ts';
import type { SearchMode, SearchResult } from './shim/engine.runtime.js';
import type { PackStats } from './pack.ts';
import type { SelfTestReport } from './selftest.ts';

const SAMPLE_URL = '/samples/docs-site.zip';
const SETTINGS_KEY = 'searchpack.settings.v1';

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const el = {
  dropzone: $('dropzone'),
  fileInput: $<HTMLInputElement>('file-input'),
  dirInput: $<HTMLInputElement>('dir-input'),
  pickFolder: $<HTMLButtonElement>('pick-folder'),
  pickFiles: $<HTMLButtonElement>('pick-files'),
  trySample: $<HTMLButtonElement>('try-sample'),
  ingestSummary: $('ingest-summary'),
  build: $<HTMLButtonElement>('build'),
  modelNote: $('model-note'),
  urlPreview: $('url-preview'),
  pipelinePanel: $('pipeline-panel'),
  pipeline: $('pipeline'),
  resultPanel: $('result-panel'),
  verdict: $('verdict'),
  stats: $('stats'),
  download: $<HTMLButtonElement>('download'),
  copySnippet: $<HTMLButtonElement>('copy-snippet'),
  share: $<HTMLButtonElement>('share'),
  snippet: $('snippet'),
  playgroundPanel: $('playground-panel'),
  query: $<HTMLInputElement>('query'),
  searchTiming: $('search-timing'),
  results: $('results'),
  error: $('error'),
  errorText: $('error-text'),
  retry: $<HTMLButtonElement>('retry'),
  drawer: $('drawer'),
  drawerList: $('drawer-list'),
  logToggle: $('log-toggle'),
  logClose: $('log-close'),
  logCopy: $('log-copy'),
  modalHost: $('modal-host'),
  modalTitle: $('modal-title'),
  modalBody: $('modal-body'),
  modalClose: $('modal-close'),
  tooltip: $('tooltip'),
  optDims: $<HTMLSelectElement>('opt-dims'),
  optChunk: $<HTMLSelectElement>('opt-chunk'),
  optSnippet: $<HTMLSelectElement>('opt-snippet'),
  optUrl: $<HTMLSelectElement>('opt-url'),
  optBase: $<HTMLInputElement>('opt-base'),
  optStrip: $<HTMLInputElement>('opt-strip'),
  optInstall: $<HTMLInputElement>('opt-install'),
  optCode: $<HTMLInputElement>('opt-code'),
};

const log = new EventLog(el.drawer, el.drawerList, el.logToggle);
initGlossary(el.tooltip);

let sourceFiles: Ingested | null = null;
let docs: ExtractedDoc[] = [];
let zipBlob: Blob | null = null;
let snippetText = '';
let building = false;
let mode: SearchMode = 'hybrid';
let searchSeq = 0;

/* ── worker ─────────────────────────────────────────────────────────────── */

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
const send = (message: WorkerRequest): void => worker.postMessage(message);

/* ── helpers ────────────────────────────────────────────────────────────── */

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function showError(message: string): void {
  el.errorText.textContent = message;
  el.error.hidden = false;
  log.add('error', message);
}

function clearError(): void {
  el.error.hidden = true;
}

/* ── settings ───────────────────────────────────────────────────────────── */

interface Settings {
  dims: string;
  chunk: string;
  snippet: string;
  url: string;
  base: string;
  strip: string;
  install: string;
  code: boolean;
}

function readSettings(): Settings {
  return {
    dims: el.optDims.value,
    chunk: el.optChunk.value,
    snippet: el.optSnippet.value,
    url: el.optUrl.value,
    base: el.optBase.value.trim() || '/',
    strip: el.optStrip.value.trim(),
    install: el.optInstall.value.trim() || '/search/',
    code: el.optCode.checked,
  };
}

function saveSettings(): void {
  try {
    // Preferences only. Document content is never persisted.
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(readSettings()));
  } catch {
    /* private mode, or a full quota — not worth interrupting the user */
  }
}

function loadSettings(): void {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return;
    const s = JSON.parse(raw) as Partial<Settings>;
    if (s.dims) el.optDims.value = s.dims;
    if (s.chunk) el.optChunk.value = s.chunk;
    if (s.snippet) el.optSnippet.value = s.snippet;
    if (s.url) el.optUrl.value = s.url;
    if (s.base) el.optBase.value = s.base;
    if (typeof s.strip === 'string') el.optStrip.value = s.strip;
    if (s.install) el.optInstall.value = s.install;
    if (typeof s.code === 'boolean') el.optCode.checked = s.code;
  } catch {
    /* corrupt settings should never block the tool */
  }
}

function updateUrlPreview(): void {
  if (docs.length === 0) {
    el.urlPreview.hidden = true;
    return;
  }
  const s = readSettings();
  const first = docs[0];
  const url = docUrl(first.path, { base: s.base, stripPrefix: s.strip, style: s.url as UrlStyle });
  el.urlPreview.textContent = `${first.path}  →  ${url}`;
  el.urlPreview.hidden = false;
}

/* ── ingestion ──────────────────────────────────────────────────────────── */

const nextFrame = (): Promise<void> => new Promise((r) => setTimeout(r, 0));

async function accept(result: Ingested, label: string): Promise<void> {
  clearError();
  if (result.files.length === 0) {
    sourceFiles = null;
    docs = [];
    el.build.disabled = true;
    el.ingestSummary.hidden = true;
    showError(
      `No indexable files in ${label}. Searchpack reads .md, .mdx, .html and .txt — ${result.skipped} other file(s) were skipped.`,
    );
    return;
  }

  sourceFiles = result;
  log.add('info', `Reading ${result.files.length} file(s) from ${label} (${humanBytes(result.bytes)})`);

  const includeCode = el.optCode.checked;
  docs = [];
  let empty = 0;
  for (let i = 0; i < result.files.length; i++) {
    const f = result.files[i];
    try {
      const doc = extractDoc(f.path, f.text, { includeCode });
      if (doc.sections.length > 0) docs.push(doc);
      else empty++;
    } catch (err) {
      empty++;
      log.add('warn', `Could not parse ${f.path}: ${(err as Error).message}`);
    }
    // Yield so a few hundred files never freeze the page.
    if (i % 25 === 24) await nextFrame();
  }

  if (docs.length === 0) {
    el.build.disabled = true;
    el.ingestSummary.hidden = true;
    showError('Those files were read, but none of them contained indexable prose.');
    return;
  }

  const suggested = commonPrefix(docs.map((d) => d.path));
  if (suggested && !el.optStrip.value) {
    el.optStrip.value = suggested;
    log.add('info', `Suggested prefix to strip from URLs: ${suggested}`);
  }

  const sections = docs.reduce((n, d) => n + d.sections.length, 0);
  el.ingestSummary.textContent =
    `${docs.length} document(s), ${sections} section(s) ready.` +
    (empty > 0 ? ` ${empty} file(s) had no prose.` : '') +
    (result.skipped > 0 ? ` ${result.skipped} unsupported file(s) skipped.` : '');
  el.ingestSummary.hidden = false;
  el.build.disabled = false;
  log.add('good', `${docs.length} document(s) parsed, ${sections} section(s)`);
  updateUrlPreview();
  saveSettings();
}

async function guarded(label: string, run: () => Promise<Ingested>): Promise<void> {
  try {
    await accept(await run(), label);
  } catch (err) {
    const message = (err as Error).message ?? String(err);
    if ((err as Error).name === 'AbortError') return; // the user closed the picker
    showError(`Could not read ${label}: ${message}`);
  }
}

/* ── pipeline UI ────────────────────────────────────────────────────────── */

const STAGES: { key: WorkerStage; label: string }[] = [
  { key: 'model', label: 'Embedding table' },
  { key: 'chunk', label: 'Chunking' },
  { key: 'embed', label: 'Embedding chunks' },
  { key: 'pack', label: 'Packing' },
  { key: 'verify', label: 'Verifying' },
];

const stageNodes = new Map<WorkerStage, { li: HTMLElement; fill: HTMLElement; note: HTMLElement; pct: HTMLElement }>();

function renderPipeline(): void {
  el.pipeline.replaceChildren();
  stageNodes.clear();
  for (const stage of STAGES) {
    const li = document.createElement('li');
    li.className = 'stage';

    const dot = document.createElement('span');
    dot.className = 'stage-dot';

    const body = document.createElement('div');
    body.className = 'stage-body';
    const labelRow = document.createElement('div');
    labelRow.className = 'stage-label';
    const name = document.createElement('span');
    name.textContent = stage.label;
    const note = document.createElement('span');
    note.className = 'stage-note';
    labelRow.append(name, note);
    const bar = document.createElement('div');
    bar.className = 'bar';
    const fill = document.createElement('div');
    fill.className = 'bar-fill';
    bar.append(fill);
    body.append(labelRow, bar);

    const pct = document.createElement('span');
    pct.className = 'stage-pct';

    li.append(dot, body, pct);
    el.pipeline.append(li);
    stageNodes.set(stage.key, { li, fill, note, pct });
  }
}

function updateStage(stage: WorkerStage, done: number, total: number, note: string): void {
  const node = stageNodes.get(stage);
  if (!node) return;
  const ratio = total > 0 ? Math.min(1, done / total) : 0;
  node.fill.style.width = `${(ratio * 100).toFixed(1)}%`;
  node.note.textContent = note;
  node.pct.textContent = `${Math.round(ratio * 100)}%`;
  node.li.classList.add('is-active');
  if (ratio >= 1) node.li.classList.add('is-done');

  // Everything before the active stage is finished by definition.
  let seen = false;
  for (const s of STAGES) {
    if (s.key === stage) {
      seen = true;
      continue;
    }
    if (!seen) {
      const prev = stageNodes.get(s.key);
      if (prev) {
        prev.li.classList.add('is-done');
        prev.fill.style.width = '100%';
        prev.pct.textContent = '100%';
      }
    }
  }
}

/* ── results ────────────────────────────────────────────────────────────── */

function stat(label: string, value: string, sub?: string): HTMLElement {
  const wrap = document.createElement('div');
  const dt = document.createElement('dt');
  dt.textContent = label;
  const dd = document.createElement('dd');
  dd.textContent = value;
  if (sub) {
    const small = document.createElement('small');
    small.textContent = sub;
    dd.append(small);
  }
  wrap.append(dt, dd);
  return wrap;
}

function renderVerdict(report: SelfTestReport): void {
  el.verdict.className = `verdict ${report.passed ? 'is-good' : 'is-bad'}`;
  el.verdict.replaceChildren();

  const title = document.createElement('p');
  title.className = 'verdict-title';
  title.textContent = report.passed ? '✓ Verified' : '✕ Unverified — download it, but check it';
  el.verdict.append(title);

  const detail = document.createElement('p');
  detail.style.margin = '0';
  detail.textContent = report.error
    ? `The emitted shim could not be loaded: ${report.error}`
    : `The emitted search.js was loaded and run against the emitted binaries. ` +
      `Round-trip cosine ${report.parity.minCosine.toFixed(6)} across ${report.parity.checked} chunks · ` +
      `binary pre-pass kept ${(report.recall.rate * 100).toFixed(0)}% of exhaustive results · ` +
      `self-retrieval ${(report.retrieval.rate * 100).toFixed(0)}% · ` +
      `${report.robustness.checked - report.robustness.failures}/${report.robustness.checked} awkward queries handled.`;
  el.verdict.append(detail);

  if (report.notes.length > 0) {
    const ul = document.createElement('ul');
    for (const note of report.notes) {
      const li = document.createElement('li');
      li.textContent = note;
      ul.append(li);
    }
    el.verdict.append(ul);
  }
}

function renderStats(stats: PackStats, elapsedMs: number): void {
  el.stats.replaceChildren(
    stat('Download', humanBytes(stats.zipBytes), 'search.zip'),
    stat('Chunks', stats.chunks.toLocaleString(), `${stats.docs} documents`),
    stat('Index', humanBytes(stats.indexBytes), `${stats.postings.toLocaleString()} postings`),
    stat(
      'Vocabulary',
      humanBytes(stats.vocabBytes),
      `${stats.shippedRows.toLocaleString()} of ${stats.vocabTokens.toLocaleString()} rows`,
    ),
    stat('Shim', `${(stats.shimBytes / 1024).toFixed(1)} KB`, 'no dependencies'),
    stat('Built in', `${(elapsedMs / 1000).toFixed(1)} s`, 'on this device'),
  );
}

function renderResults(results: SearchResult[], elapsedMs: number, query: string): void {
  el.searchTiming.textContent = query.trim()
    ? `${results.length} result(s) in ${elapsedMs.toFixed(1)} ms · ${mode} mode`
    : '';
  el.results.replaceChildren();

  if (!query.trim()) return;
  if (results.length === 0) {
    const note = document.createElement('li');
    note.className = 'empty-note';
    note.textContent = 'Nothing matched. Every word in that query is absent from your documents.';
    el.results.append(note);
    return;
  }

  for (const r of results) {
    const li = document.createElement('li');

    const head = document.createElement('div');
    head.className = 'result-head';
    const title = document.createElement('span');
    title.className = 'result-title';
    title.textContent = r.title;
    if (r.heading) {
      const heading = document.createElement('span');
      heading.className = 'result-heading';
      heading.textContent = ` › ${r.heading}`;
      title.append(heading);
    }
    const score = document.createElement('span');
    score.className = 'result-score';
    score.textContent = r.score.toFixed(4);
    head.append(title, score);

    const url = document.createElement('div');
    url.className = 'result-url';
    url.textContent = r.url;

    const snippet = document.createElement('p');
    snippet.className = 'result-snippet';
    snippet.textContent = r.snippet;

    li.append(head, url, snippet);
    el.results.append(li);
  }
}

/* ── build ──────────────────────────────────────────────────────────────── */

function startBuild(): void {
  if (building || docs.length === 0) return;
  building = true;
  clearError();
  el.build.disabled = true;
  el.build.textContent = 'Building…';
  el.resultPanel.hidden = true;
  el.playgroundPanel.hidden = true;
  el.pipelinePanel.hidden = false;
  renderPipeline();
  saveSettings();

  const s = readSettings();
  const chunkChars = Number(s.chunk);
  send({
    type: 'build',
    docs,
    dims: Number(s.dims),
    snippetChars: Number(s.snippet),
    installPath: s.install,
    siteName: '',
    builtOn: new Date().toISOString().slice(0, 10),
    chunkOptions: {
      ...DEFAULT_CHUNK_OPTIONS,
      targetChars: chunkChars,
      overlapChars: Math.round(chunkChars * 0.17),
    },
    urlOptions: { base: s.base, stripPrefix: s.strip, style: s.url as UrlStyle },
  });
}

function finishBuild(message: Extract<WorkerResponse, { type: 'done' }>): void {
  building = false;
  el.build.disabled = false;
  el.build.textContent = 'Rebuild the pack';

  zipBlob = new Blob([message.zip as BlobPart], { type: 'application/zip' });
  const install = readSettings().install;
  const path = install.endsWith('/') ? install : `${install}/`;
  snippetText = `<script type="module">
  import { loadSearchpack } from '${path}search.js';
  const pack = await loadSearchpack('${path}');
  console.log(pack.search('how do I get started'));
</script>`;
  el.snippet.textContent = snippetText;

  renderVerdict(message.report);
  renderStats(message.stats, message.elapsedMs);
  el.resultPanel.hidden = false;
  el.playgroundPanel.hidden = false;
  el.share.hidden = typeof navigator.canShare !== 'function' || typeof navigator.share !== 'function';
  el.query.focus();
}

worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
  const msg = event.data;
  switch (msg.type) {
    case 'progress':
      updateStage(msg.stage, msg.done, msg.total, msg.note);
      break;
    case 'log':
      log.add(msg.level, msg.message);
      break;
    case 'done':
      finishBuild(msg);
      break;
    case 'results':
      // Responses can arrive out of order — a short query started later can
      // finish first. Anything but the newest is stale and would overwrite good
      // results with old ones.
      if (msg.id === searchSeq) renderResults(msg.results, msg.elapsedMs, el.query.value);
      break;
    case 'error':
      building = false;
      el.build.disabled = docs.length === 0;
      el.build.textContent = 'Build the pack';
      showError(msg.message);
      break;
  }
});

worker.addEventListener('error', (event) => {
  building = false;
  el.build.disabled = docs.length === 0;
  el.build.textContent = 'Build the pack';
  showError(`The build worker failed: ${event.message}`);
});

/* ── modals ─────────────────────────────────────────────────────────────── */

const MODALS: Record<string, { title: string; template: string }> = {
  how: { title: 'How it works', template: 'tpl-how' },
  privacy: { title: 'Privacy & threat model', template: 'tpl-privacy' },
  about: { title: 'About Searchpack', template: 'tpl-about' },
};

function openModal(key: string): void {
  const spec = MODALS[key];
  if (!spec) return;
  const tpl = document.getElementById(spec.template) as HTMLTemplateElement | null;
  if (!tpl) return;
  el.modalTitle.textContent = spec.title;
  el.modalBody.replaceChildren(tpl.content.cloneNode(true));
  el.modalHost.hidden = false;
  el.modalClose.focus();
}

function closeModal(): void {
  el.modalHost.hidden = true;
  el.modalBody.replaceChildren();
}

/* ── wiring ─────────────────────────────────────────────────────────────── */

for (const btn of document.querySelectorAll<HTMLElement>('[data-modal]')) {
  btn.addEventListener('click', () => openModal(btn.dataset.modal as string));
}
el.modalClose.addEventListener('click', closeModal);
el.modalHost.querySelector('[data-close]')?.addEventListener('click', closeModal);

el.logToggle.addEventListener('click', () => log.toggleOpen());
el.logClose.addEventListener('click', () => log.close());
el.logCopy.addEventListener('click', () => {
  void navigator.clipboard?.writeText(log.text).then(
    () => {
      el.logCopy.textContent = 'Copied';
      setTimeout(() => (el.logCopy.textContent = 'Copy'), 1400);
    },
    () => log.add('warn', 'The clipboard is not available in this browser'),
  );
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    if (!el.modalHost.hidden) closeModal();
    else if (log.isOpen) log.close();
    return;
  }
  if (event.key === '/' && document.activeElement !== el.query && !el.playgroundPanel.hidden) {
    event.preventDefault();
    el.query.focus();
  }
});

el.dropzone.addEventListener('click', (event) => {
  if ((event.target as HTMLElement).closest('button')) return;
  el.fileInput.click();
});
el.dropzone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    el.fileInput.click();
  }
});

for (const type of ['dragenter', 'dragover'] as const) {
  el.dropzone.addEventListener(type, (event) => {
    event.preventDefault();
    el.dropzone.classList.add('is-dragging');
  });
}
for (const type of ['dragleave', 'dragend'] as const) {
  el.dropzone.addEventListener(type, () => el.dropzone.classList.remove('is-dragging'));
}
el.dropzone.addEventListener('drop', (event) => {
  event.preventDefault();
  el.dropzone.classList.remove('is-dragging');
  if (!event.dataTransfer) return;
  const dt = event.dataTransfer;
  void guarded('the dropped folder', () => fromDataTransfer(dt));
});

el.pickFiles.addEventListener('click', (event) => {
  event.stopPropagation();
  el.fileInput.click();
});
el.pickFolder.addEventListener('click', (event) => {
  event.stopPropagation();
  if (supportsDirectoryPicker()) void guarded('that folder', fromDirectoryPicker);
  else el.dirInput.click();
});
el.trySample.addEventListener('click', (event) => {
  // Without this the click also bubbles to the drop zone and opens the picker.
  event.stopPropagation();
  void guarded('the sample', () => fromSample(SAMPLE_URL));
});

el.fileInput.addEventListener('change', () => {
  if (el.fileInput.files?.length) {
    const files = el.fileInput.files;
    void guarded('those files', () => fromFileList(files));
  }
  el.fileInput.value = '';
});
el.dirInput.addEventListener('change', () => {
  if (el.dirInput.files?.length) {
    const files = el.dirInput.files;
    void guarded('that folder', () => fromFileList(files));
  }
  el.dirInput.value = '';
});

for (const control of [el.optDims, el.optChunk, el.optSnippet, el.optUrl, el.optInstall]) {
  control.addEventListener('change', saveSettings);
}
for (const control of [el.optBase, el.optStrip]) {
  control.addEventListener('input', () => {
    updateUrlPreview();
    saveSettings();
  });
}
el.optUrl.addEventListener('change', updateUrlPreview);
el.optCode.addEventListener('change', () => {
  saveSettings();
  if (sourceFiles) void accept(sourceFiles, 'the same files');
});

el.build.addEventListener('click', startBuild);
el.retry.addEventListener('click', () => {
  clearError();
  if (docs.length > 0) startBuild();
});

el.download.addEventListener('click', () => {
  if (!zipBlob) return;
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'search.zip';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  log.add('good', 'search.zip downloaded');
});

el.copySnippet.addEventListener('click', () => {
  void navigator.clipboard?.writeText(snippetText).then(
    () => {
      el.copySnippet.textContent = 'Copied';
      setTimeout(() => (el.copySnippet.textContent = 'Copy the snippet'), 1400);
    },
    () => log.add('warn', 'The clipboard is not available in this browser'),
  );
});

el.share.addEventListener('click', () => {
  if (!zipBlob) return;
  const file = new File([zipBlob], 'search.zip', { type: 'application/zip' });
  if (navigator.canShare?.({ files: [file] })) {
    void navigator.share({ files: [file], title: 'Searchpack index' }).catch(() => undefined);
  }
});

let searchTimer: number | undefined;
function runSearch(): void {
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => {
    searchSeq += 1;
    send({ type: 'search', query: el.query.value, mode, id: searchSeq });
  }, 120);
}
el.query.addEventListener('input', runSearch);
el.query.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') {
    window.clearTimeout(searchTimer);
    searchSeq += 1;
    send({ type: 'search', query: el.query.value, mode, id: searchSeq });
  }
});

for (const btn of document.querySelectorAll<HTMLButtonElement>('.mode-btn')) {
  btn.addEventListener('click', () => {
    mode = btn.dataset.mode as SearchMode;
    for (const other of document.querySelectorAll('.mode-btn')) other.classList.remove('is-active');
    btn.classList.add('is-active');
    runSearch();
  });
}

/* ── boot ───────────────────────────────────────────────────────────────── */

loadSettings();
if (!supportsDirectoryPicker()) {
  el.pickFolder.title = 'Uses a folder upload — this browser has no directory picker';
}
log.add('info', 'Ready. Nothing is uploaded — every step below runs in this tab.');
if (typeof caches !== 'undefined') {
  void caches
    .has('searchpack-model-v1')
    .then((hit) => {
      if (!hit) return;
      el.modelNote.textContent = 'The embedding table is already cached — this works offline.';
      log.add('good', 'Embedding table found in the local cache');
    })
    .catch(() => undefined);
}
